import { ComponentProps, ElementType, useCallback } from 'react';

import { ApiFilestackImageData } from 'api/types/ApiFilestackImageData';
import { ImagePicker } from 'components/ImagePicker';
import ShowConfirmationModal from 'containers/ShowConfirmationModal';
import { wrapPromise } from 'utils/functional';

import { Field } from './Field/Field';

type Props = {
  altText?: string;
  aspectRatio?: number;
  defaultImage?: ElementType;
  hideRemove?: boolean;
  id?: string;
  name: string;
  label?: string;
  description?: string;
  previewCrop?: boolean;
  previewCropToCircle?: boolean;
  previewWidth?: number;
  previewHeight?: number;
  format?: 'logo' | 'banner' | 'avatar';
  message?: string;
  buttonOnly?: boolean;
  centerButton?: boolean;
  overlayButton?: boolean;
  disableCrop?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  confirmModal?: Record<string, any>;
  'data-qa-id'?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (...args: Array<any>) => any;
  noMargin?: boolean;
  inlineLabel?: boolean;
  recommendedDimensions?: {
    width: number;
    height: number;
  };
  extraAnalyticsData?: Record<string, unknown>;
  insecureSVG?: boolean;
  image?: ApiFilestackImageData | null;
  saving?: boolean;
};

export function ImagePickerField({
  altText,
  aspectRatio,
  defaultImage,
  name,
  label,
  description,
  previewWidth,
  previewHeight,
  format,
  message,
  centerButton,
  overlayButton,
  disableCrop,
  confirmModal,
  onChange,
  'data-qa-id': qaId,
  recommendedDimensions,
  inlineLabel,
  noMargin,
  extraAnalyticsData,
  insecureSVG,
  previewCropToCircle,
  previewCrop,
  id,
  hideRemove,
  buttonOnly,
  image,
  saving,
}: Props) {
  const Picker = useCallback(
    (
      pickerProps: Pick<
        ComponentProps<typeof ImagePicker>,
        'data-qa-id' | 'image' | 'showConfirmationModal' | 'onChange'
      >,
    ) => (
      <ImagePicker
        altText={altText}
        defaultImage={defaultImage}
        extraAnalyticsData={extraAnalyticsData}
        name={name}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        format={format}
        message={message}
        centerButton={centerButton}
        overlayButton={overlayButton}
        disableCrop={disableCrop}
        insecureSVG={insecureSVG}
        aspectRatio={aspectRatio}
        previewCropToCircle={previewCropToCircle}
        previewCrop={previewCrop}
        id={id}
        hideRemove={hideRemove}
        buttonOnly={buttonOnly}
        recommendedDimensions={recommendedDimensions}
        label={label}
        data-qa-id={pickerProps['data-qa-id']}
        image={pickerProps.image}
        showConfirmationModal={pickerProps.showConfirmationModal}
        onChange={pickerProps.onChange}
        saving={saving}
      />
    ),
    [
      altText,
      aspectRatio,
      buttonOnly,
      centerButton,
      defaultImage,
      disableCrop,
      extraAnalyticsData,
      format,
      hideRemove,
      id,
      insecureSVG,
      label,
      message,
      name,
      overlayButton,
      previewCrop,
      previewCropToCircle,
      previewHeight,
      previewWidth,
      recommendedDimensions,
      saving,
    ],
  );

  return (
    <Field
      name={name}
      label={label}
      inlineLabel={inlineLabel}
      noMargin={noMargin}
      description={description}
      renderInput={({ value, qaId: autoQaId, updateValue }) =>
        confirmModal ? (
          // @ts-expect-error TS(2739): Type '{ onConfirm: (...args: any) => Promise<void>... Remove this comment to see the full error message
          <ShowConfirmationModal
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...confirmModal}
            onConfirm={wrapPromise(() => {
              if (onChange) {
                onChange(null);
              } else {
                updateValue(null);
              }
            })}
            render={({ showModal: showConfirmationModal }) => (
              <Picker
                showConfirmationModal={showConfirmationModal}
                image={typeof image !== 'undefined' ? image : value}
                data-qa-id={qaId || autoQaId}
                onChange={onChange || updateValue}
              />
            )}
          />
        ) : (
          <Picker
            image={typeof image !== 'undefined' ? image : value}
            data-qa-id={qaId || autoQaId}
            onChange={onChange || updateValue}
          />
        )
      }
    />
  );
}
